import React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faAngleLeft,
  faAngleRight,
  faEllipsisH,
} from "@fortawesome/free-solid-svg-icons";
import { Link } from "gatsby";

const Pagination = ({ pageInfo, makeLinkForPage, onPageChange }) => {
  if (!pageInfo || pageInfo.pageCount === 1) {
    return null;
  }

  const pages = makePagination({
    count: pageInfo.pageCount,
    page: pageInfo.currentPage,
    onChange: onPageChange,
  });
  const pagination = pages.items.map(page => {
    if (page.type === "previous") {
      if (pageInfo.hasPreviousPage) {
        return (
          <li
            key="previous"
            className="page-item"
            onClick={e => {
              if (onPageChange) {
                e.preventDefault();
                e.stopPropagation();
                onPageChange(pageInfo.currentPage - 1);
              }
            }}
          >
            <Link
              to={makeLinkForPage(pageInfo.currentPage - 1)}
              className="page-link"
            >
              {" "}
              <FontAwesomeIcon
                icon={faAngleLeft}
                className=""
                fixedWidth
                size="sm"
              />
            </Link>
          </li>
        );
      } else {
        return (
          <li key="previous" className="page-item disabled">
            <span className="page-link">
              {" "}
              <FontAwesomeIcon
                icon={faAngleLeft}
                className="text-muted"
                fixedWidth
                size="sm"
              />
            </span>
          </li>
        );
      }
    } else if (page.type === "page") {
      if (page.page === pageInfo.currentPage) {
        return (
          <li key={page.page} className="page-item active">
            <span className="page-link">{page.page}</span>
          </li>
        );
      } else {
        return (
          <li
            key={page.page}
            className="page-item"
            onClick={e => {
              if (onPageChange) {
                e.preventDefault();
                e.stopPropagation();
                onPageChange(page.page);
              }
            }}
          >
            <Link to={makeLinkForPage(page.page)} className="page-link">
              {page.page}
            </Link>
          </li>
        );
      }
    } else if (page.type === "end-ellipsis" || page.type === "start-ellipsis") {
      return (
        <li key={page.type} className="page-item disabled">
          <span className="page-link">
            {" "}
            <FontAwesomeIcon
              icon={faEllipsisH}
              className="text-muted"
              fixedWidth
              size="sm"
            />
          </span>
        </li>
      );
    } else if (page.type === "next") {
      if (pageInfo.hasNextPage) {
        return (
          <li
            key="next"
            className="page-item"
            onClick={e => {
              if (onPageChange) {
                e.preventDefault();
                e.stopPropagation();
                onPageChange(pageInfo.currentPage + 1);
              }
            }}
          >
            <Link
              to={makeLinkForPage(pageInfo.currentPage + 1)}
              className="page-link"
            >
              {" "}
              <FontAwesomeIcon
                icon={faAngleRight}
                className=""
                fixedWidth
                size="sm"
              />
            </Link>
          </li>
        );
      } else {
        return (
          <li key="next" className="page-item disabled">
            <span className="page-link">
              {" "}
              <FontAwesomeIcon
                icon={faAngleRight}
                className="text-muted"
                fixedWidth
                size="sm"
              />
            </span>
          </li>
        );
      }
    }
  });
  return (
    <nav aria-label="Page navigation">
      <ul className="pagination pagination-template d-flex justify-content-center">
        {pagination}
      </ul>
    </nav>
  );
};
export default Pagination;

// https://github.com/mui-org/material-ui/tree/master/packages/material-ui-lab/src/Pagination

function makePagination(props = {}) {
  // keep default values in sync with @default tags in Pagination.propTypes
  const {
    boundaryCount = 1,
    componentName = "usePagination",
    count = 1,
    defaultPage = 1,
    disabled = false,
    hideNextButton = false,
    hidePrevButton = false,
    onChange: handleChange,
    page: page,
    showFirstButton = false,
    showLastButton = false,
    siblingCount = 1,
    ...other
  } = props;

  const handleClick = (event, value) => {
    if (handleChange) {
      handleChange(event, value);
    }
  };

  // https://dev.to/namirsab/comment/2050
  const range = (start, end) => {
    const length = end - start + 1;
    return Array.from({ length }, (_, i) => start + i);
  };

  const startPages = range(1, Math.min(boundaryCount, count));
  const endPages = range(
    Math.max(count - boundaryCount + 1, boundaryCount + 1),
    count
  );

  const siblingsStart = Math.max(
    Math.min(
      // Natural start
      page - siblingCount,
      // Lower boundary when page is high
      count - boundaryCount - siblingCount * 2 - 1
    ),
    // Greater than startPages
    boundaryCount + 2
  );

  const siblingsEnd = Math.min(
    Math.max(
      // Natural end
      page + siblingCount,
      // Upper boundary when page is low
      boundaryCount + siblingCount * 2 + 2
    ),
    // Less than endPages
    endPages[0] - 2
  );

  // Basic list of items to render
  // e.g. itemList = ['first', 'previous', 1, 'ellipsis', 4, 5, 6, 'ellipsis', 10, 'next', 'last']
  const itemList = [
    ...(showFirstButton ? ["first"] : []),
    ...(hidePrevButton ? [] : ["previous"]),
    ...startPages,

    // Start ellipsis
    // eslint-disable-next-line no-nested-ternary
    ...(siblingsStart > boundaryCount + 2
      ? ["start-ellipsis"]
      : boundaryCount + 1 < count - boundaryCount
      ? [boundaryCount + 1]
      : []),

    // Sibling pages
    ...range(siblingsStart, siblingsEnd),

    // End ellipsis
    // eslint-disable-next-line no-nested-ternary
    ...(siblingsEnd < count - boundaryCount - 1
      ? ["end-ellipsis"]
      : count - boundaryCount > boundaryCount
      ? [count - boundaryCount]
      : []),

    ...endPages,
    ...(hideNextButton ? [] : ["next"]),
    ...(showLastButton ? ["last"] : []),
  ];

  // Map the button type to its page number
  const buttonPage = type => {
    switch (type) {
      case "first":
        return 1;
      case "previous":
        return page - 1;
      case "next":
        return page + 1;
      case "last":
        return count;
      default:
        return null;
    }
  };

  // Convert the basic item list to PaginationItem props objects
  const items = itemList.map(item => {
    return typeof item === "number"
      ? {
          onClick: event => {
            handleClick(event, item);
          },
          type: "page",
          page: item,
          selected: item === page,
          disabled,
          "aria-current": item === page ? "true" : undefined,
        }
      : {
          onClick: event => {
            handleClick(event, buttonPage(item));
          },
          type: item,
          page: buttonPage(item),
          selected: false,
          disabled:
            disabled ||
            (item.indexOf("ellipsis") === -1 &&
              (item === "next" || item === "last" ? page >= count : page <= 1)),
        };
  });

  return {
    items,
    ...other,
  };
}
